import { signOut as firebaseSignOut } from "firebase/auth";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { announceAdminSignOut } from "@/auth/adminCrossTabSignOut";
import { getFirebaseAuth } from "@/auth/helloCashflowDomain/firebase";
import { clearAllManualSubscriptionAdjustmentDrafts } from "@/components/organisations/SubscriptionManagementModal/tabs/manualSubscriptionAdjustmentDraftStorage";
import memoizedAuth from "@/lib/firebase/MemoizedAuth";
import { clearRetainedManualCheckoutForm } from "@/pages/SubscriptionDashboard/manualCheckoutActionMemory";
import { setUID } from "@/redux/userSlice";
import {
  type ApprovedAdminExit,
  getAdminDepartureHistory,
} from "@/routing/adminDepartureRuntime";
import {
  acquireAdminSignOutLock,
  showAdminSignOutFailure,
} from "@/routing/adminSignOutLock";
import { requestBroadcastExit } from "@/routing/broadcastLeaveGuard";
import { apiSlice } from "@/store/api/baseApi";
import { useAppDispatch } from "@/store/store";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";
import { resetNavigationTrail } from "@/vnext/shared/navigationTrailStore";

interface UseAdminLogoutOptions {
  redirectTo?: string;
}

/**
 * Centralized logout handler for the admin portal.
 *
 * @remarks
 * Firebase sign-out is simple (`signOut(auth)`), but we also reset in-memory client state to avoid
 * leaking cached RTK Query data between sessions (e.g. if a different user signs in without a full reload).
 */
export function useAdminLogout(options?: UseAdminLogoutOptions) {
  const redirectTo = options?.redirectTo ?? "/login";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const logger = useMemo(
    () => FrontendLoggerFactory.getLogger().withComponent("useAdminLogout"),
    [],
  );

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const logoutInFlightRef = useRef(false);

  const performLogout = useCallback(
    async (approved: ApprovedAdminExit) => {
      if (logoutInFlightRef.current) {
        return;
      }

      const interactionLock = acquireAdminSignOutLock();
      if (!interactionLock) {
        showAdminSignOutFailure();
        logger.warn("Sign out could not lock the Admin page", {
          operation: "sign_out_lock_failed",
        });
        return;
      }
      logoutInFlightRef.current = true;
      setIsLoggingOut(true);

      try {
        // Tell other open Admin tabs why their session is ending (SLK-03).
        announceAdminSignOut();
        // Best practice: call Firebase's `signOut(auth)` (docs: https://firebase.google.com/docs/auth/web/password-auth#next_steps).
        // We defensively sign out both auth instances used in this codebase (domain DI + legacy wrapper).
        const signOutResults = await Promise.allSettled([
          (async () => firebaseSignOut(getFirebaseAuth()))(),
          (async () => memoizedAuth.signOut())(),
        ]);

        const rejected = signOutResults.find(
          (result) => result.status === "rejected",
        );

        if (rejected) {
          logger.warn("Sign out had one or more failures", {
            operation: "sign_out_partial_failure",
          });
        }
      } catch (error) {
        logger.error(
          "Sign out failed",
          error instanceof Error ? error : undefined,
          { operation: "sign_out_failed" },
        );
      } finally {
        let restorationReady = false;
        let cleanupComplete = false;
        try {
          // Clear in-memory identity + cached API data before redirecting.
          clearAllManualSubscriptionAdjustmentDrafts();
          clearRetainedManualCheckoutForm();
          resetNavigationTrail();
          dispatch(setUID(""));
          dispatch(apiSlice.util.resetApiState());
          cleanupComplete = true;
          restorationReady =
            await getAdminDepartureHistory().waitForRestoration();
        } finally {
          logoutInFlightRef.current = false;
          setIsLoggingOut(false);
          if (!cleanupComplete) interactionLock.recover();
        }
        // Keep the native lock until the scoped continuation actually commits;
        // no await or editable gap exists between release and approved redirect.
        if (
          !restorationReady ||
          !approved.run(() => {
            interactionLock.release();
            void navigate(redirectTo, { replace: true });
          })
        ) {
          interactionLock.recover();
          logger.warn("Sign out redirect could not safely continue", {
            operation: "sign_out_redirect_blocked",
          });
        }
      }
    },
    [dispatch, logger, navigate, redirectTo],
  );

  const logout = useCallback(
    async () => requestBroadcastExit(performLogout),
    [performLogout],
  );

  return { logout, isLoggingOut };
}
